<sf-loading-spinner [isLoading]="isLoading()"></sf-loading-spinner>

<div class="container register-container rounded-5 my-2 my-md-5">
    <div class="row">
        <div class="col-md-6 col-lg-7 p-3 p-md-4 p-lg-5">
            <form [formGroup]="registerForm">
                <img class="mb-4" src="assets/images/surefire-logo.svg" alt="Surefire Logo" />
                <h3>Create your Surefire account</h3>
                <p class="text-body-secondary">Let's get started with your 30 days free trial</p>

                <div class="p-3 mb-3 rounded-3 bg-secondary">
                    @if (!hasDataFromAccountsIntegrator()) {
                        <h5>Using Xero accounts software?</h5>
                        <p class="mb-2">Connect your Xero account to streamline your registration process.</p>
                        <div class="d-flex">
                            <a href="{{ apiUrl }}registration/xero/signup">
                                <img src="assets/xero/connect-blue.svg" alt="Xero Connect logo" class="me-2" />
                            </a>
                        </div>
                    } @else {
                        <h5>Welcome {{ this.accountsIntegratorName() }}</h5>
                        <p class="fw-bold mb-1">We've pre-filled your details from Xero.</p>
                        <p class="mb-0 small">
                            Upon completion of your registration, you will be redirected to your Xero account to finish
                            the setup.
                        </p>
                    }
                </div>

                <h4 class="text-dark">About your company</h4>

                <div class="row g-2">
                    <div class="col-12">
                        <sf-label label="Select your Package" for="packageId" [required]="true"></sf-label>
                        <ng-select
                            id="packageId"
                            [items]="packages()"
                            bindValue="id"
                            bindLabel="name"
                            [clearable]="false"
                            formControlName="selectedPackageId">
                            <ng-template ng-option-tmp let-item="item">
                                {{ item.name }} ({{ item.costPerUserPerMonth | currency }} per user per
                                month)</ng-template
                            >
                        </ng-select>

                        @if (
                            (registerForm.controls.selectedPackageId.touched ||
                                registerForm.controls.selectedPackageId.dirty) &&
                            registerForm.controls.selectedPackageId.errors
                        ) {
                            <p class="small text-danger mb-0">Please select a package.</p>
                        }

                        <p class="small mt-2 mb-0">
                            Checkout
                            <a href="https://www.surefiresoftware.co.uk/pricing/" target="_blank"
                                >https://www.surefiresoftware.co.uk/pricing/</a
                            >
                            for more details.
                        </p>
                    </div>

                    <div class="col-sm-8">
                        <sf-input label="Company Name" inputId="companyName" formControlName="companyName"></sf-input>
                    </div>
                    <div class="col-sm-4">
                        <sf-input
                            label="SMS Name"
                            description="The name customers see when SMS are received by your company using Surefire"
                            inputId="companySms"
                            formControlName="companySms"
                            [customErrorMessages]="{ pattern: 'There must be no spaces.' }"></sf-input>
                    </div>
                    <div class="col-12">
                        <sf-input
                            label="Website Address"
                            description="Your company Website URL (if you have one)"
                            inputId="webAddress"
                            formControlName="webAddress"></sf-input>
                    </div>
                    <div class="col-12">
                        <sf-input
                            label="Wolseley account number"
                            inputId="wukNumber"
                            formControlName="wukNumber"></sf-input>
                        <button class="btn btn-sm btn-secondary" (click)="openModal(wolseley)">
                            Why do I need my account number?
                        </button>
                        <button class="btn btn-sm btn-danger" (click)="openModal(nowolseley)">
                            Don't have a wolseley account?
                        </button>
                    </div>

                    @if (wukNumber()) {
                        <div class="col-12">
                            <div class="card">
                                <div class="card-body">
                                    <p class="text-primary">
                                        By registering to use Wolseley UK services via the Surefire application you
                                        accept that Wolseley UK Limited may hold and process your personal data in
                                        respect of providing these services to you. The data you submit is collected for
                                        the purpose of identifying you and monitoring delivery of Wolseley services to
                                        you via the Surefire application. Please see Wolseley's
                                        <a
                                            class="fw-bold text-dark"
                                            href="https://www.wolseley.co.uk/policies/"
                                            target="_blank"
                                            >privacy policy</a
                                        >
                                        for further information on they use your personal data. All transactions for the
                                        purchases of goods from Wolseley UK Limited shall be subject to Wolseley’s
                                        standard conditions of business found
                                        <a
                                            class="fw-bold text-dark"
                                            href="https://wolseley-terms.co.uk/"
                                            target="_blank"
                                            >here</a
                                        >.
                                    </p>
                                    <div class="form-check">
                                        <input
                                            class="form-check-input"
                                            type="checkbox"
                                            id="wukTermsAccepted"
                                            formControlName="wukTermsAccepted" />
                                        <label class="form-check-label" for="wukTermsAccepted">
                                            I confirm that I have read and agree to the privacy policy and the
                                            terms.</label
                                        >
                                    </div>
                                </div>
                            </div>
                        </div>
                    }

                    <div class="col-12">
                        <div class="form-check">
                            <input
                                class="form-check-input"
                                type="checkbox"
                                id="vatRegistered"
                                formControlName="vatRegistered" />
                            <label class="form-check-label" for="vatRegistered"> I am VAT Registered </label>
                        </div>
                    </div>
                    <div class="col-12">
                        <sf-input label="VAT Number" inputId="vatNumber" formControlName="vatNumber"></sf-input>
                    </div>
                </div>

                <h4 class="text-dark mt-3">About you</h4>

                <sf-input label="First Name" inputId="firstName" formControlName="firstName"></sf-input>
                <sf-input label="Last Name" inputId="lastName" formControlName="lastName"></sf-input>

                <sf-input
                    label="Username"
                    inputId="username"
                    formControlName="username"
                    [customErrorMessages]="{ usernameTaken: 'Username has already been taken' }"></sf-input>
                <sf-input
                    label="Email"
                    inputId="email"
                    formControlName="email"
                    [customErrorMessages]="{ emailTaken: 'Email has already been used' }"></sf-input>
                <sf-input label="Mobile Number" inputId="phoneNumber" formControlName="phoneNumber"></sf-input>

                <div class="border border-1 p-3 my-3 rounded-3" formGroupName="address">
                    <p class="fw-bold">Your Address</p>
                    <div [hidden]="!gotAddress()">
                        <sf-input
                            label="Address Line 1"
                            inputId="addressLine1"
                            formControlName="addressLine1"></sf-input>
                        <sf-input
                            label="Address Line 2"
                            inputId="addressLine2"
                            formControlName="addressLine2"></sf-input>
                        <sf-input
                            label="Address Line 3"
                            inputId="addressLine3"
                            formControlName="addressLine3"></sf-input>
                        <sf-input
                            label="Address Line 4"
                            inputId="addressLine4"
                            formControlName="addressLine4"></sf-input>
                        <sf-input label="Locality" inputId="locality" formControlName="locality"></sf-input>
                        <sf-input label="Town" inputId="town" formControlName="town"></sf-input>
                        <sf-input label="County" inputId="county" formControlName="county"></sf-input>
                    </div>

                    <sf-postcode-lookup
                        label="Postcode"
                        inputId="postcode"
                        formControlName="postcode"
                        (onAddressChange)="updateAddress($event)"></sf-postcode-lookup>

                    <!-- <sf-label for="postcode" label="Postcode" [required]="true"></sf-label>
                    <div class="input-group mb-3">
                        <input type="text" class="form-control" formControlName="postcode" />
                        <button
                            class="btn btn-secondary"
                            type="button"
                            id="button-addon2"
                            [disabled]="registerForm.controls.address.controls.postcode.invalid"
                            (click)="findAddresses()">
                            Find
                        </button>
                    </div>

                    @if (addressError()) {
                        <p class="small text-danger">
                            {{ addressError() }}
                        </p>
                    }

                    @if (addressSuggestions().length > 0) {
                        <ng-select
                            placeholder="Please select an address"
                            [items]="addressSuggestions()"
                            bindLabel="address"
                            bindValue="id"
                            (change)="getAddress($event)"></ng-select>
                    } -->
                </div>
                <sf-input label="Promotion/Referral Code" inputId="promoCode" formControlName="promoCode"></sf-input>

                <div class="mt-4 px-5">
                    <p class="small text-center">
                        By submitting this form you agree to our Terms & Conditions and
                        <a href="https://www.surefiresoftware.co.uk/privacy-policy/" target="_blank">Privacy Policy</a>.
                    </p>
                    <button class="w-100 btn btn-lg btn-secondary" type="submit" (click)="submit()">
                        Register
                        @if (isLoading()) {
                            <div class="spinner-border spinner-border-sm">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                        }
                    </button>

                    <p class="small text-center my-3">Already have an account? <a routerLink="/login">Log in</a></p>
                </div>
            </form>
        </div>
        <div class="col-md-6 col-lg-5 bg-primary p-3 p-md-4 p-lg-5 rounded-end-5">
            <h2 class="text-secondary mb-4">Why Surefire?</h2>
            <p class="text-light fw-light">
                Job planning, customer communications and tedious administration can be just as time-consuming for a
                small business as it is for a large one. Surefire takes the hassle out of running your business, does
                all the administrative work, and leaves you free to do the job you love, when and how you want to.
            </p>
            <h2 class="text-secondary mb-4">Key benefits for small businesses</h2>
            <ul class="benefits-list">
                <li>
                    <div class="d-flex align-items-center">
                        <div class="fw-light">Manage appointments on the go and automatically update customers.</div>
                        <div>
                            <fa-icon class="text-secondary" size="lg" icon="check-circle"></fa-icon>
                        </div>
                    </div>
                </li>
                <li>
                    <div class="d-flex align-items-center">
                        <div class="fw-light">
                            Produce bespoke, professional quotations using your favourites products with your merchant
                            prices and send to customers instantly.
                        </div>
                        <div>
                            <fa-icon class="text-secondary" size="lg" icon="check-circle"></fa-icon>
                        </div>
                    </div>
                </li>
                <li>
                    <div class="d-flex align-items-center">
                        <div class="fw-light">
                            No more chasing payments: take payment on site through our chip & pin reader.
                        </div>
                        <div>
                            <fa-icon class="text-secondary" size="lg" icon="check-circle"></fa-icon>
                        </div>
                    </div>
                </li>
                <li>
                    <div class="d-flex align-items-center">
                        <div class="fw-light">
                            Automatically generate invoices, certificates and annual service reminders.
                        </div>
                        <div>
                            <fa-icon class="text-secondary" size="lg" icon="check-circle"></fa-icon>
                        </div>
                    </div>
                </li>
                <li>
                    <div class="d-flex align-items-center">
                        <div class="fw-light">
                            Regain your work-life balance: vastly reduce administrative paperwork, ensuring evenings and
                            weekends are not lost to work.
                        </div>
                        <div>
                            <fa-icon class="text-secondary" size="lg" icon="check-circle"></fa-icon>
                        </div>
                    </div>
                </li>
            </ul>
            <h2 class="text-secondary mb-4">What our customers say...</h2>

            <p class="text-light">
                <i class="fw-light"
                    >"It has revolutionised our job booking systems, we have gone from 7 paper diaries to 1 single
                    system! This connects our office staff with our engineers via iPads and our customers allowing the
                    office staff to communicate with both. It has given us the ability to search for addresses, jobs and
                    documentation without the need for trolling through books and papers which saves us time, we make
                    less mistakes and it gives us a much more professional approach. It was so easy to implement, the
                    system was personalised for our business and the back-up service is excellent."</i
                >

                <br />
                <br />
                <b>Sharon Surtees, Director</b><br />
                J Shipley & Co Heating Ltd
            </p>
        </div>
    </div>
</div>

<ng-template #successModal let-modal>
    <div class="modal-header">
        <h4 class="modal-title" id="modal-basic-title">Registration Complete</h4>
    </div>
    <div class="modal-body">
        <p>Thank you for registering with Surefire.</p>
        <!-- @if (hasDataFromAccountsIntegrator()) {
            <p>You will be redirected to your Xero account to complete the setup.</p>
        } -->
        <p class="fw-bold">You will receive an email shortly. Closing this message will take you to the login page.</p>
    </div>
    <div class="modal-footer">
        <button type="button" class="btn btn-secondary" (click)="modal.close()">Login Now</button>
    </div>
</ng-template>

<ng-template #wolseley let-modal>
    <div class="modal-header">
        <h4 class="modal-title text-wolseley" id="modal-basic-title">Connect Surefire to your account</h4>
        <button type="button" class="btn-close" aria-label="Close" (click)="modal.dismiss('Cross click')"></button>
    </div>
    <div class="modal-body">
        <!-- <h2>Success!</h2> -->
        <div class="row align-items-center">
            <div class="col-md-12">
                <p>
                    Wolseley UK is a leading plumbing, heating and cooling trade specialist merchant which aims to be
                    the first choice for trade customers across the UK. We take pride in offering excellent specialist
                    know-how, being easy to do business with, offering confidence that the price is right and making
                    sure we deliver on our promises.
                </p>
            </div>
            <div class="col-md-6">
                <h4 class="text-wolseley mb-4">Trading with Wolseley couldn’t be easier</h4>
                <ul class="basic-list">
                    <li>More products in stock from one specialist merchant</li>
                    <li>Our exclusive range for great quality and value</li>
                    <li>Easier and earlier collections</li>
                </ul>

                <h4 class="text-wolseley mb-4">Connect to Wolseley with Surefire. Work smarter, not harder</h4>
                <ul class="basic-list">
                    <li>Locate and contact your local Wolseley branch – over 450 branches nationwide</li>
                    <li>Check live stock availability in any of our branches or distribution centres</li>
                    <li>View your live personalised trade account prices</li>
                    <li>Order for collection from any branch or we deliver direct to your home, office or site</li>
                </ul>
            </div>
            <div class="col-md-6">
                <div class="bg-wolseley p-3 text-light">
                    <h3 class="text-light pb-3">How do I connect my Wolseley account with surefire?</h3>
                    <ul class="basic-list">
                        <li>
                            Email Surefire support with your Wolseley account number or enter your account number when
                            signing up
                        </li>
                        <li>Wolseley will verify and activate your trade account</li>
                        <li>We will email you, when your Wolseley trade account is linked with Surefire</li>
                        <li>
                            Start checking live stock availability, viewing your live personalised trade prices and
                            place orders directly for collection or delivery
                        </li>
                    </ul>
                </div>
            </div>
        </div>
        <div class="row">
            <div class="col-12 text-center mt-5 mb-3">
                <p>
                    <strong
                        >If you have any enquiries regarding your Wolseley account, please call
                        <a href="tel:0344 292 7060">0344 292 7060</a> or email
                        <a href="mailto:customer.data@wolseley.co.uk">customer.data&#64;wolseley.co.uk</a></strong
                    >
                </p>
            </div>

            <div class="col-4 text-center">
                <img src="/assets/images/wolseley/image-1.png" alt="" class="img-fluid mx-auto" />
            </div>
            <div class="col-4 text-center">
                <img src="/assets/images/wolseley/image-2.png" alt="" class="img-fluid mx-auto" />
            </div>
            <div class="col-4 text-center">
                <img src="/assets/images/wolseley/image-3.png" alt="" class="img-fluid center" />
            </div>
        </div>
        <div class="row mt-3">
            <div class="col-4 text-center">
                <img src="/assets/images/wolseley/icons.png" alt="" class="img-fluid mx-auto" />
            </div>
            <div class="col-4 text-center"></div>
            <div class="col-4 text-center">
                <img src="/assets/images/wolseley/logo.png" alt="" class="img-fluid center" />
            </div>
        </div>
    </div>
    <div class="modal-footer">
        <button type="button" class="btn btn-secondary" (click)="modal.close('Save click')">Close</button>
    </div>
</ng-template>

<ng-template #nowolseley let-modal>
    <div class="modal-header">
        <h4 class="modal-title text-wolseley" id="modal-basic-title">New to Wolseley?</h4>
        <button type="button" class="btn-close" aria-label="Close" (click)="modal.dismiss('Cross click')"></button>
    </div>
    <div class="modal-body">
        <!-- <h2>Success!</h2> -->
        <div class="row align-items-center">
            <div class="col-md-12">
                <p>
                    Wolseley UK is a leading plumbing, heating and cooling trade specialist merchant which aims to be
                    the first choice for trade customers across the UK. We take pride in offering excellent specialist
                    know-how, being easy to do business with, offering confidence that the price is right and making
                    sure we deliver on our promises.
                </p>
            </div>
            <div class="col-md-6">
                <h4 class="text-wolseley mb-4">
                    Trading with Wolseley couldn’t be easier – open a trade account today and discover the benefits
                </h4>
                <ul class="basic-list">
                    <li>More products in stock from one specialist merchant</li>
                    <li>Our exclusive range for great quality and value</li>
                    <li>Easier and earlier collections</li>
                </ul>

                <h4 class="text-wolseley mb-4">Connect to Wolseley with Surefire. Work smarter, not harder</h4>
                <ul class="basic-list">
                    <li>Locate and contact your local Wolseley branch – over 450 branches nationwide</li>
                    <li>Check live stock availability in any of our branches or distribution centres</li>
                    <li>View your live personalised trade account prices</li>
                    <li>Order for collection from any branch or we deliver direct to your home, office or site</li>
                </ul>
            </div>
            <div class="col-md-6">
                <div class="bg-wolseley p-3 text-light rounded-3">
                    <h3 class="text-light pb-3">How do I open a trade account?</h3>
                    <ul class="basic-list">
                        <li>
                            <a
                                class="btn btn-sm btn-dark"
                                href="https://www.wolseley.co.uk/open-account/?utm_source=Surefire&amp;utm_medium=Referral&amp;utm_campaign=ecom+%7C+Surefire+%7C+Partner&amp;utm_id=Surefire&amp;utm_content=registration+page"
                                target="_blank"
                                download=""
                                >Sign up quickly and easily online here</a
                            >
                        </li>
                        <li>Review the information you will need to hand when completing the form</li>
                        <li>Click 'Start' to begin your application</li>
                        <li>Your application will be processed within 5 working days</li>
                        <li>Wolseley will email the primary account contact when your new trade account is open</li>
                        <li>
                            Email Surefire support with your Wolseley account number or enter your account number when
                            signing up
                        </li>
                    </ul>
                </div>
            </div>
        </div>
        <div class="row">
            <div class="col-12 text-center mt-5 mb-3">
                <p>
                    <strong
                        >If you have any enquiries regarding your Wolseley account, please call
                        <a href="tel:0344 292 7060">0344 292 7060</a> or email
                        <a href="mailto:customer.data@wolseley.co.uk">customer.data&#64;wolseley.co.uk</a></strong
                    >
                </p>
            </div>

            <div class="col-4 text-center">
                <img src="/assets/images/wolseley/image-1.png" alt="" class="img-fluid mx-auto" />
            </div>
            <div class="col-4 text-center">
                <img src="/assets/images/wolseley/image-2.png" alt="" class="img-fluid mx-auto" />
            </div>
            <div class="col-4 text-center">
                <img src="/assets/images/wolseley/image-3.png" alt="" class="img-fluid center" />
            </div>
        </div>
        <div class="row mt-3">
            <div class="col-4 text-center">
                <img src="/assets/images/wolseley/icons.png" alt="" class="img-fluid mx-auto" />
            </div>
            <div class="col-4 text-center"></div>
            <div class="col-4 text-center">
                <img src="/assets/images/wolseley/logo.png" alt="" class="img-fluid center" />
            </div>
        </div>
    </div>
    <div class="modal-footer">
        <button type="button" class="btn btn-secondary" (click)="modal.close('Save click')">Close</button>
    </div>
</ng-template>
